/**
 * ActiveSessionsPanel — Profile-page widget listing every device that
 * has ever signed in to this account + a per-row "Revoke" button for
 * surgical device-level logout.
 *
 * v167.x — slimmed from 354 → ~150 lines by extracting the per-device
 * row + helpers into ./sessions/DeviceRow.jsx. This file now owns
 * fetch, edit, and revoke state only.
 *
 * Distinct from `SignInActivityPanel`:
 *   - SignInActivityPanel: chronological feed (login attempts,
 *     magic-links, lockouts, etc).
 *   - ActiveSessionsPanel: live device roster — each row represents
 *     one browser fingerprint the JWT `did` claim is bound to.
 *
 * Backed by:
 *   GET    /api/auth/devices                  → list { devices, current_device_id }
 *   PATCH  /api/auth/devices/{device_id}      → rename (label)
 *   DELETE /api/auth/devices/{device_id}      → revoke (per-device kill)
 *
 * If the user revokes the device they're sitting on, the backend
 * deletes both the `session_token` and `ve_device_id` cookies; the next
 * navigation lands them on /login.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import DeviceRow from './sessions/DeviceRow';

export default function ActiveSessionsPanel({ isDark = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);

  /* eslint-disable -- react-hooks/set-state-in-effect false-positive on the fetch effect. */
  const load = () => {
    setLoading(true);
    api.get('/auth/devices')
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load devices'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  /* eslint-enable */

  const revoke = async (device) => {
    const msg = device.is_current
      ? 'Revoke THIS device? You will be signed out of this browser immediately. You can sign back in afterwards.'
      : `Sign out "${device.label || device.device_id}"? Any active session on that device will be revoked immediately.`;
    if (!window.confirm(msg)) return;
    setPendingId(device.device_id);
    try {
      const { data: r } = await api.delete(`/auth/devices/${device.device_id}`);
      if (r?.is_current) {
        try {
          const { clearToken } = await import('@/lib/tokenStore');
          clearToken();
        } catch { /* ignore */ }
        toast.success('Device revoked. Redirecting to sign-in…');
        setTimeout(() => { window.location.href = '/login'; }, 700);
        return;
      }
      toast.success('Device signed out.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally { setPendingId(null); }
  };

  const startEdit = (device) => {
    setEditingId(device.device_id);
    setEditValue(device.label || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(''); };
  const saveEdit = async (device) => {
    const next = (editValue || '').trim();
    if (!next) return toast.error('Label cannot be empty.');
    if (next === device.label) { cancelEdit(); return; }
    setPendingId(device.device_id);
    try {
      await api.patch(`/auth/devices/${device.device_id}`, { label: next });
      toast.success('Renamed.');
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    } finally { setPendingId(null); }
  };

  const allDevices = data?.devices || [];
  const revokedDevices = allDevices.filter((d) => d.is_revoked);
  const visibleDevices = showRevoked ? allDevices : allDevices.filter((d) => !d.is_revoked);

  return (
    <div
      className="pt-6 border-t border-slate-200 dark:border-white/10"
      data-testid="active-sessions-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[var(--ve-violet-bright)]" />
          <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
            isDark ? 'text-[#D4AF37]' : 'text-[#7A5A00]'
          }`}>
            [ACCOUNT · ACTIVE DEVICES]
          </span>
        </div>
        <div className="flex items-center gap-2">
          {revokedDevices.length > 0 && (
            <button
              onClick={() => setShowRevoked((v) => !v)}
              data-testid="toggle-revoked-devices"
              className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5"
            >
              {showRevoked ? 'Hide' : 'Show'} revoked ({revokedDevices.length})
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="devices-refresh"
            className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            REFRESH
          </button>
        </div>
      </div>

      <h2 className="display text-xl mt-2">Signed in on these devices.</h2>
      <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
        One row per browser that holds an active session for your
        account. Don&apos;t recognize one? Revoke it — it kills only
        that device&apos;s session, the others stay signed in.
      </p>

      <div className="mt-5">
        {loading ? (
          <div className="text-xs text-slate-500 italic py-4 text-center">Loading devices…</div>
        ) : visibleDevices.length === 0 ? (
          <div
            className="text-xs italic py-4 text-center text-slate-500 dark:text-white/55"
            data-testid="devices-empty"
          >
            No active devices on file.
          </div>
        ) : (
          <ul
            className="space-y-1.5 max-h-[28rem] overflow-y-auto pr-1 ve-scrollbar"
            data-testid="devices-list"
          >
            {visibleDevices.map((d) => (
              <DeviceRow
                key={d.device_id}
                device={d}
                isDark={isDark}
                isEditing={editingId === d.device_id}
                editValue={editValue}
                isPending={pendingId === d.device_id}
                onEditChange={setEditValue}
                onStartEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSave={saveEdit}
                onRevoke={revoke}
              />
            ))}
          </ul>
        )}
      </div>

      <p className="text-[10px] text-slate-500 dark:text-white/40 italic mt-3 leading-relaxed">
        Revoking a single device kills only its session. To sign out
        every browser at once (including this one&apos;s siblings), use
        <em> Sign out everywhere else </em> below.
      </p>
    </div>
  );
}
